/**
 * Crew specialty — the one label map, the one badge map, the one hue map.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * `specialty` is a real Postgres enum with four values, defined in the API at
 * `src/db/schema.ts:71`:
 *
 *     employee_specialty = tong_technician | computer_technician
 *                        | thread_wash_technician | thread_supervisor
 *
 * The frontend did not know that. It typed the column as `specialty?: string`
 * and classified it in one place — `crewGroup()` in screens/DispatchScreen.tsx —
 * with a substring match against a hand-written list:
 *
 *     WASH_SPECIALTY_KEYS = ['thread_wash_technician', 'thread_wash', 'vacuum_operator']
 *
 * TWO OF THOSE THREE KEYS ARE DEAD. `thread_wash` is a redundant prefix of
 * `thread_wash_technician` and can never match on its own; `vacuum_operator` is
 * not an enum value at all and never was. So the list did the work of one key,
 * and the classification it produced was binary: `thread_wash_technician` was
 * "wash" and the OTHER THREE SPECIALTIES WERE ALL "run" — one colour for a Tong
 * Technician, a Computer Technician and a Thread Supervisor alike. "No specialty
 * set" was a third group in the type but painted identically to run, so it was
 * a fourth thing rendering as the same colour as three others.
 *
 * A dispatcher could not tell a Tong Technician from a Thread Supervisor, or
 * either from someone unassigned, anywhere on the board.
 *
 * Per CLAUDE.md: labels come from a shared exhaustive map keyed on every enum
 * value, never a binary check. `Record<CrewSpecialty, …>` rather than
 * `Record<string, …>` is the point — a fifth specialty in the enum fails the
 * typecheck here instead of silently rendering as "run".
 *
 * NULL IS NOT A SPECIALTY and is deliberately outside the union. It is a real
 * state (the column is nullable) and gets its own neutral treatment at each call
 * site rather than a fifth map entry, so that "unassigned" can never be mistaken
 * for a specialty somebody chose.
 */

/** Every value of the API's `employee_specialty` enum. */
export type CrewSpecialty =
  | 'tong_technician'
  | 'computer_technician'
  | 'thread_wash_technician'
  | 'thread_supervisor';

export const CREW_SPECIALTY_LABELS: Record<CrewSpecialty, string> = {
  tong_technician: 'Tong Technician',
  computer_technician: 'Computer Technician',
  thread_wash_technician: 'Thread Wash Technician',
  thread_supervisor: 'Thread Supervisor',
};

/**
 * ONE PERSON, ONE HUE, EVERYWHERE — AND THE BADGE IS WHAT KEEPS THE TWO
 * SURFACES APART NOW.
 *
 * In the week view, Ines Duarte appears TWICE on the same screen: in the CREW
 * roster column, and on every job card she is assigned to. Both are filled with
 * the same sky fill for her role. THIS USED TO BE TWO ENCODINGS — hue in the
 * roster, a neutral chip with a letter badge on the card — and the reason was a
 * measured collision rather than a design idea: the roster carries no job
 * status, a card carries a 4px status stripe, and `--crew-tong` was
 * byte-identical to `--status-on-hold` in dark. A hue on a card meant a chip
 * wearing the colour of the stripe beneath it.
 *
 * THE PALETTE WAS THE FIX, NOT THE PLACEMENT. Every `--crew-*` fill is now held
 * CIEDE2000 dE >= 12 from every `--status-*` value in both themes and from the
 * five `--fi-signal-*` hues the board spends elsewhere, so there is no surface
 * here where a crew chip can be read as a status. See the `--crew-*` section of
 * theme.css for the seventeen-way measurement and
 * `src/styles/crew-tint-contrast.test.ts` for it recomputed on every run.
 *
 * WHAT THE LETTER BADGE IS FOR NOW, WHICH IS NOT WHAT IT WAS FOR. It used to be
 * the ONLY carrier of the specialty on a card, because the card had no hue. It
 * is now the carrier of a different distinction: an on-card chip shows its
 * badge and a roster chip does not, so a placed hand and a draggable one still
 * read as two objects. That replaces the outlined-versus-filled pair, which
 * this change spends — both chips are filled.
 *
 * SO DO NOT "TIDY" EITHER HALF:
 *   - dropping the badge from a card chip merges placed and draggable into
 *     one shape, which is the affordance #738 was careful not to delete;
 *   - adding the badge to a roster chip repeats the specialty heading printed
 *     two lines above it AND removes the same distinction from the other end.
 *
 * The roster column is grouped by specialty and prints the specialty as its
 * group heading, so the name of the thing is already on screen there. A job
 * card states no specialty anywhere, which is why the badge belongs on that
 * side rather than the other.
 *
 * Pinned by `DispatchScreen.card.test.tsx` — a card chip carries its role fill
 * AND its badge, a roster chip carries the fill and no badge.
 */

/**
 * The letter badge on an on-card crew chip.
 *
 * A BADGE **AND** A HUE, WHICH IS A CHANGE FROM WHAT THIS SAID. It used to be a
 * badge INSTEAD of a hue: the dispatch board already carried six status hues,
 * two queue accents and the wash card's blue, and the crew hues of the day made
 * that worse rather than better — a wash chip resolved to #60a5fa in dark,
 * byte-identical to `--status-pending`. The hues are re-picked and measured
 * clear of all of it now, so the card gets the fill; the badge stays because it
 * is what tells a placed chip from a roster chip, and because a letter is
 * legible to a reader who cannot separate two of the fills.
 *
 * `tracking-wide` on the rendered span because these are all-caps abbreviations
 * at 10px, where the default tracking closes up glyph pairs. It is a legibility
 * aid and NOT what makes the four distinguishable — that is the letter set
 * itself.
 *
 * WHY `TNG` AND NOT `TT` FOR TONG. `TT` is a SUBSEQUENCE of `TWT` — every letter
 * of `TT`, in order, appears inside `TWT`. At chip size a single dropped glyph
 * turns one into the other, and no font-size or letter-spacing tweak fixes a
 * containment relationship; it only makes the wrong reading easier to see. The
 * four badges were checked for duplicates, subsequence containment and shared
 * prefixes: CT/TS/TWT/TNG is clean on all three, CT/TS/TWT/TT is not.
 *
 * `CT` rather than `C` for the same family of reason — a bare `C` reads as a
 * category rather than a role, and breaks the initials pattern the other three
 * follow.
 */
export const CREW_SPECIALTY_BADGE: Record<CrewSpecialty, string> = {
  tong_technician: 'TNG',
  computer_technician: 'CT',
  thread_wash_technician: 'TWT',
  thread_supervisor: 'TS',
};

/**
 * The four specialty fills, ONE VALUE PER ROLE, spent on BOTH surfaces.
 *
 * FILLED, AND ON THE JOB CARDS TOO. Read the "ONE PERSON, ONE HUE" note above
 * before changing either half: the roster chip and the on-card chip now carry
 * the same fill, and the on-card chip adds the letter badge that keeps a placed
 * hand distinguishable from a draggable one.
 *
 * THE HUE IS THE FILL AND THE NAME ON IT IS NEAR-BLACK (`--fi-signal-ink`, the
 * token already measured against five signal fills). That is what lets one
 * value serve both themes: an ink has to clear 4.5 against two opposite card
 * surfaces and no colour does, but a fill carries its ink with it. See the
 * `--crew-*` section of theme.css for the four measurements.
 *
 * ── WHY THESE ARE TOKENS AND NOT PALETTE UTILITIES ────────────────────────
 *
 * AN EARLIER VERSION OF THIS NOTE CLAIMED AN EXHAUSTIVE SEARCH "against three
 * bars, in BOTH themes" giving "minimum text 4.99". THAT CLAIM WAS FALSE FOR
 * THE LIGHT HALF, and it is worth keeping on the record because the sentence is
 * what stopped anyone re-measuring for three releases: a docblock that reads as
 * a completed measurement is treated as one. The four values then in place
 * measured 5.44-9.70 as ink on the dark card and 1.51-2.69 on the light one,
 * against a floor of 4.5 — the search had been run against the dark card only.
 *
 * AND NO RE-PICK OF FOUR INKS COULD HAVE FIXED IT. For one colour to clear
 * 4.5:1 as ink on both cards its relative luminance must be <= 0.1833 (to clear
 * it on #FFFFFF) and >= 0.2730 (to clear it on #1e293b). That range is EMPTY —
 * not "no good four-hue set" but no colour of any hue. So the chip changed
 * shape rather than colour, and the ratio is now the same in both themes by
 * construction.
 *
 * ── WHAT THE HUES ARE HELD CLEAR OF ───────────────────────────────────────
 *
 * Contrast is WCAG 2.1 relative luminance; separation is CIEDE2000. Every fill
 * clears 4.5 with the near-black on it (worst 9.25, computer) and sits dE >= 12
 * from all SEVENTEEN hues this board can put beside it: six `--status-*` values
 * in dark, six in light, and the five `--fi-signal-*` hues. The realised worst
 * clearance is 14.9 and the worst mutual separation 34.1. See the crew section
 * of theme.css for the per-role figures and crew-tint-contrast.test.ts for it
 * recomputed on every run.
 *
 * BLUE IS THE WASH ROLE. That is the one association a dispatcher has already
 * learned, so the sky is pinned and the other three are picked around it.
 *
 * THE OTHER THREE ARE NO LONGER HELD TO THE WARM FAMILY, AND THAT CONSTRAINT IS
 * WHAT THE PREVIOUS PALETTE WAS PAYING FOR. The idea was that a glance should
 * read wash-versus-run, with warmth carrying the "run" half. It cost more than
 * it bought: holding three roles inside one narrow arc of the wheel — an arc the
 * scheduled and on-hold statuses already own most of — forced the supervisor
 * onto a bronze whose ink was 6.02, barely above the floor and the darkest chip
 * on the board. Dropping it and sweeping the whole wheel put every role above
 * 9.25 and widened the tightest pair from 27.5 to 34.1. The specialty is named
 * on the chip by a letter badge and spelled out by the roster's section heading,
 * so hue was never the only carrier of run-versus-wash and did not need to be.
 *
 * THE OLD NOTE SAID "NO ORANGE SURVIVES" AND THAT WAS TOO STRONG. What does not
 * survive is a SATURATED orange: from the mid shades down they collide with the
 * on-hold and scheduled statuses, which own amber and orange in dark and the
 * browns in light. A pale peach clears comfortably, and the supervisor fill is
 * one — so the role that the old search sent to a bronze, and the role before it
 * that went to pink, both had a warm option available the whole time. The
 * durable claim is the narrow one. It is on the record because the broad version
 * was sound enough to survive a review and steered two palettes, and what
 * disproved it was re-running the search without the constraint rather than
 * re-reading the sentence.
 *
 * Class names are literals for the reason job-status.ts spells its own out:
 * Tailwind's JIT only sees literal strings, and a constructed class emits no
 * CSS with a missing colour as the only symptom.
 */
export const CREW_SPECIALTY_TINT: Record<CrewSpecialty, string> = {
  tong_technician: 'bg-crew-tong text-fi-signal-ink',
  computer_technician: 'bg-crew-computer text-fi-signal-ink',
  thread_wash_technician: 'bg-crew-wash text-fi-signal-ink',
  thread_supervisor: 'bg-crew-supervisor text-fi-signal-ink',
};

/**
 * The treatment for a crew member with no specialty set, on either surface.
 *
 * It carries its own 1px edge, which the four tinted chips do not need and the
 * shared chip base no longer supplies: the tinted chips are shapes because they
 * have a fill, and this one has none. A fill here would have to be a fifth
 * colour meaning "no colour", which is the placeholder-that-reads-as-data
 * mistake the badge half of this module already refuses. On a card such a
 * member also renders no badge, so both channels say the same absent thing.
 */
export const CREW_SPECIALTY_NEUTRAL = 'border border-fieldiq-border text-fieldiq-text';

/**
 * Display order. Supervisor first, then the three technician roles — the order a
 * dispatcher reads a crew in. Members with no specialty sort last, handled at the
 * call site since `null` is outside the union.
 */
export const CREW_SPECIALTY_ORDER: Record<CrewSpecialty, number> = {
  thread_supervisor: 0,
  tong_technician: 1,
  computer_technician: 2,
  thread_wash_technician: 3,
};

const KNOWN: ReadonlySet<string> = new Set(Object.keys(CREW_SPECIALTY_LABELS));

/**
 * Coerce whatever the API actually sent onto the enum, or `null`.
 *
 * The normalisation is inherited from the `crewGroup()` this replaced, which
 * lower-cased and collapsed spaces and hyphens onto underscores so that
 * "Thread Wash Technician" and "thread-wash-technician" both land on the enum
 * key. That tolerance is kept because it costs nothing and the column has been
 * written by more than one path.
 *
 * WHAT IS DELIBERATELY NOT KEPT is the substring match. `crewGroup()` used
 * `.includes()`, which meant any unrecognised value containing "thread_wash"
 * silently became a wash technician. An exact match against the enum is what
 * makes the map exhaustive rather than merely long: a value the frontend has
 * never seen returns `null` and renders as unassigned, which is visible, instead
 * of being absorbed into a real specialty, which is not.
 */
export function normalizeSpecialty(raw: unknown): CrewSpecialty | null {
  if (typeof raw !== 'string') return null;
  const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return KNOWN.has(key) ? (key as CrewSpecialty) : null;
}

/** Letter badge for a specialty off the wire, or `null` when none is set. */
export function crewSpecialtyBadge(raw: unknown): string | null {
  const s = normalizeSpecialty(raw);
  return s ? CREW_SPECIALTY_BADGE[s] : null;
}

/** Human label for a specialty off the wire. Absent renders as an em dash. */
export function crewSpecialtyLabel(raw: unknown): string {
  const s = normalizeSpecialty(raw);
  return s ? CREW_SPECIALTY_LABELS[s] : '—';
}

/** Chip classes for a specialty off the wire; unset gets the neutral. */
export function crewSpecialtyTint(raw: unknown): string {
  const s = normalizeSpecialty(raw);
  return s ? CREW_SPECIALTY_TINT[s] : CREW_SPECIALTY_NEUTRAL;
}

/** Sort rank; anything without a specialty sorts after all four. */
export function crewSpecialtyRank(raw: unknown): number {
  const s = normalizeSpecialty(raw);
  return s ? CREW_SPECIALTY_ORDER[s] : 99;
}

/**
 * THE PHASE A HAND IS PRE-FILLED WITH when the office ticks them onto a job
 * that has a wash date (CrewAssignmentScreen). The one list of which
 * specialties count as a thread washer — nothing else in the app decides it.
 *
 * Exhaustive over the enum on purpose: a fifth specialty fails the typecheck
 * here instead of quietly defaulting. Only `thread_wash_technician` is a
 * thread washer. `thread_supervisor` is Run — the operator's rule names thread
 * washers only, and everyone else, blank and unrecognised included, is Run.
 *
 * A PRE-FILL, NEVER A FILTER AND NEVER AN OVERWRITE. It is shown on the hand's
 * row and the office can change it to Wash, Run or Both before saving. A hand
 * with a saved phase keeps it. No hand is hidden by specialty — a tong hand
 * filling in on a wash stays assignable.
 *
 * Measured read-only 2026-11-03: every user on TNT, PAR, Dash, Dutch and
 * Derek's has NO specialty, so on the live tenants this pre-fills Run for
 * everyone until specialties are set. Smith and Lone Star (demo) carry all four
 * values.
 */
export const CREW_SPECIALTY_DEFAULT_PHASE: Record<CrewSpecialty, 'wash' | 'run'> = {
  thread_wash_technician: 'wash',
  tong_technician: 'run',
  computer_technician: 'run',
  thread_supervisor: 'run',
};

/** Pre-filled phase for a specialty off the wire. Blank or unknown = Run. */
export function defaultPhaseForSpecialty(raw: unknown): 'wash' | 'run' {
  const s = normalizeSpecialty(raw);
  return s ? CREW_SPECIALTY_DEFAULT_PHASE[s] : 'run';
}
